// permission.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged, map } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PermissionService {
    private readonly STORAGE_KEY = 'permissionsList';

    private readonly _permissions$ = new BehaviorSubject<string[]>(
        this.readFromStorage()
    );
    readonly permissions$: Observable<string[]> = this._permissions$.asObservable();

    constructor() {
        // Persist to localStorage whenever the list changes (after normalize).
        this.permissions$
            .pipe(
                map((list) => this.normalize(list)),
                distinctUntilChanged((a, b) => this.arrayEqual(a, b))
            )
            .subscribe((list) => this.writeToStorage(list));

        // Cross-tab sync
        if (this.hasStorage()) {
            window.addEventListener('storage', (e) => {
                if (e.key === this.STORAGE_KEY) {
                    this.updateIfChanged(this.readFromStorage());
                }
            });
        }
    }

    /** Current snapshot */
    get value(): string[] {
        return this._permissions$.value;
    }

    /** Set (will normalize + persist) */
    set(value: string[] | null | undefined) {
        this._permissions$.next(this.normalize(value));
    }

    /** Update only if content differs (order-insensitive) */
    updateIfChanged(next: string[] | null | undefined) {
        const clean = this.normalize(next);
        if (!this.arrayEqual(this.value, clean)) {
            this._permissions$.next(clean);
        }
    }

    /** Synchronous checks */
    has(perm: string): boolean {
        return this.value.includes(perm);
    }
    hasAny(perms: string[]): boolean {
        return perms.some((p) => this.has(p));
    }
    hasAll(perms: string[]): boolean {
        return perms.every((p) => this.has(p));
    }

    /** Reactive checks for templates (use with | async) */
    hasAny$(perms: string[]): Observable<boolean> {
        return this.permissions$.pipe(
            map((list) => perms.some((p) => list.includes(p))),
            distinctUntilChanged()
        );
    }
    hasAll$(perms: string[]): Observable<boolean> {
        return this.permissions$.pipe(
            map((list) => perms.every((p) => list.includes(p))),
            distinctUntilChanged()
        );
    }

    // ---------- helpers ----------

    private normalize(value: unknown): string[] {
        if (!Array.isArray(value)) return [];
        // keep only non-empty strings, trim, dedupe, sort (stable representation)
        const cleaned = value
            .filter((v): v is string => typeof v === 'string')
            .map((s) => s.trim())
            .filter(Boolean);
        return Array.from(new Set(cleaned)).sort();
    }

    private arrayEqual(a: string[], b: string[]): boolean {
        if (a === b) return true;
        if (!a || !b || a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
        return true;
    }

    private hasStorage(): boolean {
        return typeof window !== 'undefined' && !!window.localStorage;
    }

    private readFromStorage(): string[] {
        if (!this.hasStorage()) return [];
        try {
            const raw = localStorage.getItem(this.STORAGE_KEY);
            return this.normalize(raw ? JSON.parse(raw) : []);
        } catch {
            return [];
        }
    }

    private writeToStorage(list: string[]) {
        if (!this.hasStorage()) return;
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(list));
        } catch {
            // ignore quota/blocked storage errors
        }
    }
}